import React, { useEffect, useState, createContext, useContext, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "th_admin_token";

// Auth uses a JWT in the Authorization header (localStorage), NOT cookies.
// withCredentials must stay false: a credentialed request forces the browser
// to reject any wildcard `Access-Control-Allow-Origin`, which surfaced as a
// "Network Error" on the production login.
axios.defaults.withCredentials = false;

// ---- Auth context ----
const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined=loading, null=guest, obj=admin
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);

  const fetchMe = useCallback(async (tok) => {
    try {
      const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
      const r = await axios.get(`${API}/auth/me`, { headers });
      setUser(r.data);
    } catch {
      setUser(null);
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      fetchMe(token);
    } else {
      setUser(null);
    }
  }, [token, fetchMe]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    const tok = r.data.token;
    localStorage.setItem(TOKEN_KEY, tok);
    axios.defaults.headers.common.Authorization = `Bearer ${tok}`;
    setToken(tok);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* noop */ }
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common.Authorization;
    setToken(null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, token, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
